/**
 * Maps validation failures from `PUT …/contributions/{id}/details` back to
 * form fields (GIFTWEB-16 AC 9).
 *
 * The API answers a 400 with a single string, e.g.
 *   "legalFirstName is required; Invalid phone number format.; relationshipToChild is invalid"
 * so each `;`-separated segment is attributed to a field by name (or by a
 * phone/email/relationship keyword when the segment doesn't name the field).
 * Anything that can't be placed is returned in `unmapped` for a form-level
 * message instead of a toast.
 */

export const DETAILS_FIELDS = [
  'legalFirstName',
  'legalLastName',
  'email',
  'phone',
  'relationshipToChild',
  'nameKnownAs',
] as const;

export type DetailsField = (typeof DETAILS_FIELDS)[number];

export const DETAILS_FIELD_LABELS: Record<DetailsField, string> = {
  legalFirstName: 'Legal first name',
  legalLastName: 'Legal last name',
  email: 'Email address',
  phone: 'Mobile number',
  relationshipToChild: 'Relationship',
  nameKnownAs: 'Name the child knows you by',
};

// Friendlier copy for the API's known verbatim messages.
const KNOWN_MESSAGES: Array<[RegExp, DetailsField, string]> = [
  // The API still only accepts UK numbers (backend "accept international
  // phone numbers" ticket not yet raised), so a valid foreign number lands
  // here too — point at the way through rather than blaming the input.
  [
    /^invalid phone number format\.?$/i,
    'phone',
    "We couldn't save this mobile number. Please check it, or leave it blank to continue.",
  ],
  [/^invalid email\b.*$/i, 'email', 'Please enter a valid email address'],
  [
    /^relationshipToChild is invalid\.?$/i,
    'relationshipToChild',
    'Please select a relationship from the list',
  ],
];

const KEYWORD_FIELDS: Array<[RegExp, DetailsField]> = [
  [/\b(phone|mobile)\b/i, 'phone'],
  [/\be-?mail\b/i, 'email'],
  [/\brelationship\b/i, 'relationshipToChild'],
  [/\bfirst name\b/i, 'legalFirstName'],
  [/\blast name\b/i, 'legalLastName'],
];

export type MappedDetailsErrors = {
  fields: Partial<Record<DetailsField, string>>;
  unmapped: string[];
};

export function mapDetailsApiErrors(
  message: string | undefined | null,
): MappedDetailsErrors {
  const fields: Partial<Record<DetailsField, string>> = {};
  const unmapped: string[] = [];

  for (const raw of String(message ?? '').split(';')) {
    const segment = raw.trim();
    if (!segment) continue;

    const known = KNOWN_MESSAGES.find(([re]) => re.test(segment));
    if (known) {
      fields[known[1]] ??= known[2];
      continue;
    }

    // Field names are matched case-sensitively (the API echoes the camelCase
    // key) so natural-language words like "Phone"/"Email" fall through to the
    // keyword pass and keep their wording.
    const byName = DETAILS_FIELDS.find((f) =>
      new RegExp(`\\b${f}\\b`).test(segment),
    );
    const byKeyword = KEYWORD_FIELDS.find(([re]) => re.test(segment))?.[1];
    const field = byName ?? byKeyword;

    if (!field) {
      unmapped.push(segment);
      continue;
    }

    const text = byName
      ? segment.replace(
          new RegExp(`\\b${byName}\\b`),
          DETAILS_FIELD_LABELS[byName],
        )
      : segment;
    fields[field] ??= text.replace(/\.$/, '');
  }

  return { fields, unmapped };
}
